import AddIcon from '@mui/icons-material/Add';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import GppGoodOutlinedIcon from '@mui/icons-material/GppGoodOutlined';
import LiveHelpOutlinedIcon from '@mui/icons-material/LiveHelpOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import StarBorderOutlinedIcon from '@mui/icons-material/StarBorderOutlined';
import VolunteerActivismOutlinedIcon from '@mui/icons-material/VolunteerActivismOutlined';
import { TabContext, TabPanel } from '@mui/lab';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  Container,
  Grid,
  Link,
  Paper,
  Rating,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';

import FooterWave from '@/components/home/footer-wave';
import ScrollReveal from '@/components/ui/FadeUp';
import FormFinder from '@/components/ui/FormFinder';
import ReviewRatings from '@/components/ui/Reviews';
import SignalSection from '@/components/ui/SignalSection';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const BusinessTaxFilingFinder = () => {
  const [activeDeadlineTab, setActiveDeadlineTab] = useState('1');

  const handleDeadlineTabChange = (
    _event: React.SyntheticEvent,
    newValue: string
  ) => {
    setActiveDeadlineTab(newValue);
  };

  return (
    <AppLayout
      meta={
        <Meta
          title="Business Tax Filing Online: Find Forms & Deadlines"
          description="Need to file business taxes? Find the right tax forms for your business, check filing deadlines and extensions, and e-file eligible returns with Taxzerone"
          keywords=""
          canonical="business-tax-filing/"
          jsonLdPricing=""
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
          ogShareImg=""
        />
      }
    >
      {/* Hero Section */}
      <Box
        component="section"
        aria-labelledby="hero-business-tax"
        sx={{
          width: '100%',
          position: 'relative',
          isolation: 'isolate',
          pt: { xs: 5, sm: 7, md: 8 },
          pb: { xs: 6, sm: 6, md: 4 },
          px: { xs: 2, sm: 3, md: 4 },
          backgroundColor: '#ffffff',
          backgroundImage: `
            linear-gradient(
              to bottom,
              #ffffff 0%,
              rgba(255, 255, 255, 0.6) 12%,
              transparent 25%,
              transparent 65%,
              rgba(255, 255, 255, 0.6) 82%,
              #ffffff 100%
            ),
            linear-gradient(
              to right,
              transparent 0%,
              transparent 12%,
              #ffffff 30%,
              #ffffff 70%,
              transparent 88%,
              transparent 100%
            ),
            radial-gradient(
              ellipse 45% 55% at 100% 0%,
              rgba(136, 56, 248, 0.16) 0%,
              transparent 70%
            ),
            radial-gradient(
              ellipse 45% 55% at 0% 100%,
              rgba(34, 61, 153, 0.14) 0%,
              transparent 70%
            ),
            radial-gradient(
              rgba(34, 61, 153, 0.2) 1.5px,
              transparent 1.6px
            )
          `,
          backgroundSize:
            '100% 100%, 100% 100%, 100% 100%, 100% 100%, 24px 24px',
          backgroundRepeat:
            'no-repeat, no-repeat, no-repeat, no-repeat, repeat',
          backgroundPosition: 'center top',
        }}
      >
        <ScrollReveal>
          <Container
            maxWidth="xl"
            sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}
          >
            <SignalSection icon={<GppGoodOutlinedIcon fontSize="small" />}>
              IRS-Authorized E-File Provider
            </SignalSection>

            <Typography
              variant="h1"
              id="hero-business-tax"
              sx={{
                fontSize: { xs: 30, sm: 40, md: 50, lg: 54 },
                fontWeight: 700,
                lineHeight: { xs: 1.25, sm: 1.18 },
                letterSpacing: '-0.02em',
                mb: 2.5,
              }}
            >
              <span className="gradient-text">Business Tax Filing:</span> <br />
              Find Your Form & Due Date
            </Typography>

            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: 16, sm: 18, md: 20 },
                lineHeight: 1.5,
                fontWeight: 400,
                maxWidth: 920,
                mx: 'auto',
                mb: 4,
              }}
            >
              Your return depends on your business type and what it does during
              the year. Match your situation to the form, check what else you
              may need, and see when each one is due.
            </Typography>

            <Button
              variant="contained"
              className="btn-primary"
              href={`${process.env.NEXT_PUBLIC_APPLICATION_URL || ''}signup/`}
              sx={{
                borderRadius: '200px !important',
                px: 4,
                py: 1.5,
                fontSize: { xs: 16, sm: 18 },
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: '0 8px 20px rgba(33, 62, 153, 0.25)',
                transition: 'all 0.3s ease 0s',
                '&:hover, &:focus, &:active': {
                  transform: 'scale(1.05)',
                  boxShadow: '0 12px 24px rgba(33, 62, 153, 0.3)',
                },
              }}
            >
              E-File Now
            </Button>

            <Box sx={{ mt: { xs: 6, md: 8 }, maxWidth: 1180, mx: 'auto' }}>
              <Grid
                container
                spacing={{ xs: 2.5, md: 3 }}
                justifyContent="center"
              >
                {/* Card 1: Business Tax Forms */}
                <Grid size={{ xs: 12, md: 4 }}>
                  <Link
                    href="#business-tax-forms"
                    underline="none"
                    sx={{
                      position: 'relative',
                      display: 'flex',
                      textAlign: 'left',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      height: '100%',
                      p: { xs: 3, md: 3.5 },
                      borderRadius: 4,
                      bgcolor: '#ffffff',
                      border: '1px solid #E2E8F0',
                      boxShadow: '0 2px 8px rgba(15, 23, 42, 0.04)',
                      overflow: 'hidden',
                      transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 4,
                        bgcolor: '#7C3AED',
                        opacity: 0,
                        transition: 'opacity 0.25s ease',
                      },
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        borderColor: alpha('#7C3AED', 0.4),
                        boxShadow: '0 16px 30px -6px rgba(124, 58, 237, 0.15)',
                        '&::before': { opacity: 1 },
                        '& .card-icon-1': { bgcolor: '#7C3AED', color: '#fff' },
                        '& .card-arrow-1': {
                          transform: 'translateX(5px)',
                          color: '#7C3AED',
                        },
                        '& .card-action-1': { color: '#7C3AED' },
                      },
                    }}
                  >
                    <div>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ mb: 3 }}
                      >
                        <Box
                          className="card-icon-1"
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 2.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: alpha('#2563EB', 0.08),
                            color: '#2563EB',
                            transition: 'all 0.25s ease',
                          }}
                        >
                          <DescriptionOutlinedIcon sx={{ fontSize: 24 }} />
                        </Box>
                        <Chip
                          label="Classification"
                          size="small"
                          sx={{
                            fontSize: '0.72rem',
                            fontWeight: 700,
                            letterSpacing: 0.5,
                            textTransform: 'uppercase',
                            color: '#64748B',
                            bgcolor: '#F8FAFC',
                            border: '1px solid #E2E8F0',
                            borderRadius: 1.5,
                          }}
                        />
                      </Stack>

                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { xs: 18, sm: 20 },
                          fontWeight: 700,
                          color: '#0F172A',
                          letterSpacing: '-0.02em',
                          lineHeight: 1.35,
                          mb: 1.25,
                        }}
                      >
                        Which return do I file?
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          color: '#475569',
                          lineHeight: 1.6,
                          fontSize: '0.925rem',
                          mb: 3,
                        }}
                      >
                        Match your entity type to its main form.
                      </Typography>
                    </div>

                    <Box
                      sx={{
                        pt: 2,
                        borderTop: '1px dashed #E2E8F0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography
                        className="card-action-1"
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: '#334155',
                          fontSize: '0.875rem',
                          transition: 'color 0.2s ease',
                        }}
                      >
                        Business tax forms
                      </Typography>
                      <ArrowForwardRoundedIcon
                        className="card-arrow-1"
                        sx={{
                          fontSize: 18,
                          color: '#94A3B8',
                          transition: 'transform 0.25s ease, color 0.2s ease',
                        }}
                      />
                    </Box>
                  </Link>
                </Grid>

                {/* Card 2: Employment Tax Returns */}
                <Grid size={{ xs: 12, md: 4 }}>
                  <Link
                    href="#employment-tax-returns"
                    underline="none"
                    sx={{
                      position: 'relative',
                      display: 'flex',
                      textAlign: 'left',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      height: '100%',
                      p: { xs: 3, md: 3.5 },
                      borderRadius: 4,
                      bgcolor: '#ffffff',
                      border: '1px solid #E2E8F0',
                      boxShadow: '0 2px 8px rgba(15, 23, 42, 0.04)',
                      overflow: 'hidden',
                      transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 4,
                        bgcolor: '#7C3AED',
                        opacity: 0,
                        transition: 'opacity 0.25s ease',
                      },
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        borderColor: alpha('#7C3AED', 0.4),
                        boxShadow: '0 16px 30px -6px rgba(124, 58, 237, 0.15)',
                        '&::before': { opacity: 1 },
                        '& .card-icon-2': { bgcolor: '#7C3AED', color: '#fff' },
                        '& .card-arrow-2': {
                          transform: 'translateX(5px)',
                          color: '#7C3AED',
                        },
                        '& .card-action-2': { color: '#7C3AED' },
                      },
                    }}
                  >
                    <div>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ mb: 3 }}
                      >
                        <Box
                          className="card-icon-2"
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 2.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: alpha('#0D9488', 0.08),
                            color: '#0D9488',
                            transition: 'all 0.25s ease',
                          }}
                        >
                          <BadgeOutlinedIcon sx={{ fontSize: 24 }} />
                        </Box>
                        <Chip
                          label="Compliance"
                          size="small"
                          sx={{
                            fontSize: '0.72rem',
                            fontWeight: 700,
                            letterSpacing: 0.5,
                            textTransform: 'uppercase',
                            color: '#64748B',
                            bgcolor: '#F8FAFC',
                            border: '1px solid #E2E8F0',
                            borderRadius: 1.5,
                          }}
                        />
                      </Stack>

                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { xs: 18, sm: 20 },
                          fontWeight: 700,
                          color: '#0F172A',
                          letterSpacing: '-0.02em',
                          lineHeight: 1.35,
                          mb: 1.25,
                        }}
                      >
                        Which other forms do I need?
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          color: '#475569',
                          lineHeight: 1.6,
                          fontSize: '0.925rem',
                          mb: 3,
                        }}
                      >
                        Payroll, contractor and supporting forms depend on your
                        activity.
                      </Typography>
                    </div>

                    <Box
                      sx={{
                        pt: 2,
                        borderTop: '1px dashed #E2E8F0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography
                        className="card-action-2"
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: '#334155',
                          fontSize: '0.875rem',
                          transition: 'color 0.2s ease',
                        }}
                      >
                        Employment forms & information returns
                      </Typography>
                      <ArrowForwardRoundedIcon
                        className="card-arrow-2"
                        sx={{
                          fontSize: 18,
                          color: '#94A3B8',
                          transition: 'transform 0.25s ease, color 0.2s ease',
                        }}
                      />
                    </Box>
                  </Link>
                </Grid>

                {/* Card 3: Deadlines */}
                <Grid size={{ xs: 12, md: 4 }}>
                  <Link
                    href="#deadlines-for-every-form"
                    underline="none"
                    sx={{
                      position: 'relative',
                      display: 'flex',
                      textAlign: 'left',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      height: '100%',
                      p: { xs: 3, md: 3.5 },
                      borderRadius: 4,
                      bgcolor: '#ffffff',
                      border: '1px solid #E2E8F0',
                      boxShadow: '0 2px 8px rgba(15, 23, 42, 0.04)',
                      overflow: 'hidden',
                      transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 4,
                        bgcolor: '#7C3AED',
                        opacity: 0,
                        transition: 'opacity 0.25s ease',
                      },
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        borderColor: alpha('#7C3AED', 0.4),
                        boxShadow: '0 16px 30px -6px rgba(124, 58, 237, 0.15)',
                        '&::before': { opacity: 1 },
                        '& .card-icon-3': { bgcolor: '#7C3AED', color: '#fff' },
                        '& .card-arrow-3': {
                          transform: 'translateX(5px)',
                          color: '#7C3AED',
                        },
                        '& .card-action-3': { color: '#7C3AED' },
                      },
                    }}
                  >
                    <div>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ mb: 3 }}
                      >
                        <Box
                          className="card-icon-3"
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 2.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: alpha('#7C3AED', 0.08),
                            color: '#7C3AED',
                            transition: 'all 0.25s ease',
                          }}
                        >
                          <EventAvailableOutlinedIcon sx={{ fontSize: 24 }} />
                        </Box>
                        <Chip
                          label="Calendar"
                          size="small"
                          sx={{
                            fontSize: '0.72rem',
                            fontWeight: 700,
                            letterSpacing: 0.5,
                            textTransform: 'uppercase',
                            color: '#64748B',
                            bgcolor: '#F8FAFC',
                            border: '1px solid #E2E8F0',
                            borderRadius: 1.5,
                          }}
                        />
                      </Stack>

                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { xs: 18, sm: 20 },
                          fontWeight: 700,
                          color: '#0F172A',
                          letterSpacing: '-0.02em',
                          lineHeight: 1.35,
                          mb: 1.25,
                        }}
                      >
                        When is it due?
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          color: '#475569',
                          lineHeight: 1.6,
                          fontSize: '0.925rem',
                          mb: 3,
                        }}
                      >
                        Due dates, extensions and what an extension does not
                        cover.
                      </Typography>
                    </div>

                    <Box
                      sx={{
                        pt: 2,
                        borderTop: '1px dashed #E2E8F0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography
                        className="card-action-3"
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: '#334155',
                          fontSize: '0.875rem',
                          transition: 'color 0.2s ease',
                        }}
                      >
                        Deadlines for every form
                      </Typography>
                      <ArrowForwardRoundedIcon
                        className="card-arrow-3"
                        sx={{
                          fontSize: 18,
                          color: '#94A3B8',
                          transition: 'transform 0.25s ease, color 0.2s ease',
                        }}
                      />
                    </Box>
                  </Link>
                </Grid>
              </Grid>
            </Box>
          </Container>
        </ScrollReveal>
      </Box>
      <FormFinder />

      {/* 1. Business Tax forms Section */}
      <Box
        component="section"
        id="business-tax-forms"
        aria-labelledby="business-tax-forms-heading"
        sx={{
          width: '100%',
          py: { xs: 6, md: 8 },
          px: { xs: 2, sm: 3, md: 4 },
          backgroundColor: '#ffffff',
          scrollMarginTop: '80px',
        }}
      >
        <Container maxWidth="lg">
          <ScrollReveal>
            <SignalSection
              icon={<BusinessCenterOutlinedIcon fontSize="small" />}
            >
              Business Filing
            </SignalSection>
            <Typography
              variant="h2"
              id="business-tax-forms-heading"
              sx={{
                fontSize: { xs: 28, sm: 34, md: 40 },
                fontWeight: 700,
                mb: 1.5,
              }}
            >
              Business Tax forms
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontSize: { xs: 16, sm: 18 }, mb: 3 }}
            >
              For corporations, partnerships, and businesses reporting income to
              the IRS.
            </Typography>

            <Paper
              variant="outlined"
              sx={{
                p: 2.5,
                mb: 4,
                borderRadius: 3,
                backgroundColor: '#f8fafc',
                borderColor: '#e2e8f0',
              }}
            >
              <Typography
                variant="h3"
                sx={{ fontSize: { xs: 16, sm: 18 }, fontWeight: 700, mb: 1 }}
              >
                When does a business owner need this category?
              </Typography>
              <Typography variant="body1">
                When the business needs to report its income, deductions,
                credits, and other financial details on a federal or applicable
                state business income tax return.
              </Typography>
            </Paper>

            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ borderRadius: 3, borderColor: '#e1d8eb', mb: 4 }}
            >
              <Table>
                <TableHead sx={{ backgroundColor: '#f4f0ff' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, width: '35%' }}>
                      Business type or situation
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, width: '25%' }}>
                      Main form
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>
                      What it is used for
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      C corporation
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Form 1120</TableCell>
                    <TableCell>
                      Reports corporate income, deductions, and tax.
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#fafcff' }}>
                    <TableCell sx={{ fontWeight: 600 }}>
                      S corporation
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {' '}
                      <Link
                        href="/business-tax/efile-irs-form-1120-s/ "
                        sx={{ color: '#0202ff' }}
                      >
                        Form 1120-S
                      </Link>
                    </TableCell>
                    <TableCell>
                      Reports S corporation income, deductions, and other tax
                      items.
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Partnership</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {' '}
                      <Link
                        href="/business-tax/efile-irs-form-1065-online/ "
                        sx={{ color: '#0202ff' }}
                      >
                        Form 1065
                      </Link>
                    </TableCell>
                    <TableCell>
                      Reports partnership income, deductions, and partner
                      allocations.
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#fafcff' }}>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Multi-member LLC taxed as a partnership
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Form 1065</TableCell>
                    <TableCell>
                      Generally, reports partnership income.
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      LLC electing C corporation taxation
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Form 1120</TableCell>
                    <TableCell>Generally, files as a C corporation.</TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#fafcff' }}>
                    <TableCell sx={{ fontWeight: 600 }}>
                      LLC electing S corporation taxation
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Form 1120-S</TableCell>
                    <TableCell>
                      Files as an S corporation if the election is valid.
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            <Box
              sx={{
                p: 3,
                borderRadius: 3,
                border: '1px solid #e2ddf3',
                backgroundColor: '#ffffff',
              }}
            >
              <Typography
                variant="h3"
                sx={{ fontSize: { xs: 18, sm: 20 }, fontWeight: 700, mb: 2 }}
              >
                Other forms the business may need
              </Typography>
              <Box
                component="ul"
                sx={{
                  m: 0,
                  p: 0,
                  listStyle: 'none',
                  '& li': {
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                    mb: 1.5,
                  },
                }}
              >
                <li>
                  <CheckCircleRoundedIcon
                    sx={{ fontSize: 18, color: '#34C79A', mt: 0.25 }}
                  />
                  <Typography variant="body1">
                    <strong>Form 1125-A:</strong> Cost of Goods Sold.
                  </Typography>
                </li>
                <li>
                  <CheckCircleRoundedIcon
                    sx={{ fontSize: 18, color: '#34C79A', mt: 0.25 }}
                  />
                  <Typography variant="body1">
                    <strong>Form 1125-E:</strong> Compensation of Officers.
                  </Typography>
                </li>
                <li>
                  <CheckCircleRoundedIcon
                    sx={{ fontSize: 18, color: '#34C79A', mt: 0.25 }}
                  />
                  <Typography variant="body1">
                    <strong>Schedule K-1 (Form 1065):</strong> Partner's share
                    of partnership income and other items.
                  </Typography>
                </li>
                <li>
                  <CheckCircleRoundedIcon
                    sx={{ fontSize: 18, color: '#34C79A', mt: 0.25 }}
                  />
                  <Typography variant="body1">
                    <strong>Schedule K-1 (Form 1120-S):</strong> Shareholder's
                    share of S corporation income and other items.
                  </Typography>
                </li>
                <li>
                  <CheckCircleRoundedIcon
                    sx={{ fontSize: 18, color: '#34C79A', mt: 0.25 }}
                  />
                  <Typography variant="body1">
                    <strong>Form 4562:</strong> Depreciation and Amortization.
                  </Typography>
                </li>
                <li>
                  <CheckCircleRoundedIcon
                    sx={{ fontSize: 18, color: '#34C79A', mt: 0.25 }}
                  />
                  <Typography variant="body1">
                    <strong>Form 4797:</strong> Sales of Business Property.
                  </Typography>
                </li>
                <li>
                  <CheckCircleRoundedIcon
                    sx={{ fontSize: 18, color: '#34C79A', mt: 0.25 }}
                  />
                  <Typography variant="body1">
                    <strong>Form 7004:</strong> Extension of time to file
                    certain business returns; see the Extension Forms category
                    below.
                  </Typography>
                </li>
              </Box>
              <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic' }}>
                These additional forms are conditional; not every business needs
                all of them.
              </Typography>
            </Box>
          </ScrollReveal>
        </Container>
      </Box>

      {/* 2. Employment Tax Returns Section */}
      <Box
        component="section"
        id="employment-tax-returns"
        aria-labelledby="employment-tax-returns-heading"
        sx={{
          width: '100%',
          py: { xs: 6, md: 8 },
          px: { xs: 2, sm: 3, md: 4 },
          backgroundColor: '#fafcff',
          scrollMarginTop: '80px',
        }}
      >
        <Container maxWidth="lg">
          <ScrollReveal>
            <SignalSection icon={<RequestQuoteOutlinedIcon fontSize="small" />}>
              Payroll Tax Compliance
            </SignalSection>
            <Typography
              variant="h2"
              id="employment-tax-returns-heading"
              sx={{
                fontSize: { xs: 28, sm: 34, md: 40 },
                fontWeight: 700,
                mb: 1.5,
              }}
            >
              Employment Tax Returns
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontSize: { xs: 16, sm: 18 }, mb: 3 }}
            >
              For businesses that pay employees or have specific employment tax
              obligations.
            </Typography>

            <Paper
              variant="outlined"
              sx={{
                p: 2.5,
                mb: 4,
                borderRadius: 3,
                backgroundColor: '#ffffff',
                borderColor: '#e2e8f0',
              }}
            >
              <Typography
                variant="h3"
                sx={{ fontSize: { xs: 16, sm: 18 }, fontWeight: 700, mb: 1 }}
              >
                When does a business owner need this category?
              </Typography>
              <Typography variant="body1">
                When the business pays wages, withholds federal income tax, owes
                Social Security or Medicare taxes, or has other
                employment-related tax filing obligations.
              </Typography>
            </Paper>

            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ borderRadius: 3, borderColor: '#e1d8eb', mb: 3 }}
            >
              <Table>
                <TableHead sx={{ backgroundColor: '#f4f0ff' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, width: '35%' }}>
                      Business situation
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, width: '25%' }}>
                      Main form
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Purpose</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Quarterly payroll tax reporting
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <Link
                        href="/employment-tax/e-file-irs-form-941/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form 941
                      </Link>
                    </TableCell>
                    <TableCell>
                      Reports federal income tax withheld, and both the employer
                      and employee portions of Social Security and Medicare
                      taxes.
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#fafcff' }}>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Annual FUTA reporting
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <Link
                        href="/employment-tax/e-file-irs-form-940/ "
                        sx={{ color: '#0202ff' }}
                      >
                        Form 940
                      </Link>
                    </TableCell>
                    <TableCell>Reports federal unemployment tax.</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Eligible small employer filing annually
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {' '}
                      <Link
                        href="/employment-tax/e-file-irs-form-944/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form 944
                      </Link>
                    </TableCell>
                    <TableCell>
                      Annual employment tax return for small employers with
                      $1,000 or less in annual employment tax liability, as
                      instructed by the IRS
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#fafcff' }}>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Agricultural employees
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {' '}
                      <Link
                        href="/employment-tax/e-file-irs-form-943/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form 943
                      </Link>
                    </TableCell>
                    <TableCell>
                      Reports employment taxes for agricultural employees.
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Quarterly payroll tax reporting
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {' '}
                      <Link
                        href="/employment-tax/e-file-irs-form-941-sp/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form 941 SP
                      </Link>
                    </TableCell>
                    <TableCell>
                      Spanish-language version of Form 941. It is not a separate
                      tax filing requirement.
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#fafcff' }}>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Aggregate Form 941 filers, such as qualifying agents or
                      certified PEOs
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <Link
                        href="/employment-tax/e-file-irs-form-941-schedule-r/ "
                        sx={{ color: '#0202ff' }}
                      >
                        Form 941 Schedule R
                      </Link>
                    </TableCell>
                    <TableCell>
                      Allocates employment taxes among the clients of an
                      aggregate filer.
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Aggregate Form 940 filers, such as 3504 agents or
                      certified professional employer organizations (CPEOs)
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <Link
                        href="/employment-tax/e-file-irs-form-940-schedule-r/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form 940 Schedule R
                      </Link>
                    </TableCell>
                    <TableCell>
                      Allocates FUTA tax information among the clients of an
                      aggregate filer.
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#fafcff' }}>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Correcting a previously filed quarterly payroll return
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {' '}
                      <Link
                        href="/employment-tax/efile-irs-form-941-x-online/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form 941-X
                      </Link>
                    </TableCell>
                    <TableCell>
                      Adjusts or corrects Form 941 as permitted by the
                      instructions.
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Correcting an annual FUTA return
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <Link
                        href="/employment-tax/efile-irs-form-940-amendment/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form 940-Amendment
                      </Link>
                    </TableCell>
                    <TableCell>Adjusts or corrects Form 940.</TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#fafcff' }}>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Correcting an agricultural employment tax return
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {' '}
                      <Link
                        href="/employment-tax/e-file-irs-form-943-x/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form 943-X
                      </Link>
                    </TableCell>
                    <TableCell>Adjusts or corrects Form 943.</TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Reporting backup withholding or certain other withheld
                      taxes
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {' '}
                      <Link
                        href="/employment-tax/e-file-irs-form-945/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form 945
                      </Link>
                    </TableCell>
                    <TableCell>Reports nonpayroll withholding.</TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#fafcff' }}>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Reporting certain withheld taxes incorrectly
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {' '}
                      <Link
                        href="/employment-tax/e-file-irs-form-945-x/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form 945-X
                      </Link>
                    </TableCell>
                    <TableCell>Corrects Form 945.</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            <Alert
              severity="info"
              sx={{ mb: 3, borderLeft: '3px solid #0288d1' }}
            >
              <strong>Good to know:</strong> Employers can't simply choose
              between Form 941 and Form 944. The IRS determines who qualifies or
              is instructed to file Form 944. Form 945 is also separate from
              ordinary payroll tax reporting.
            </Alert>
          </ScrollReveal>
        </Container>
      </Box>

      {/* 3. Exempt Organization forms Section */}
      <Box
        component="section"
        id="exempt-organization-forms"
        aria-labelledby="exempt-org-heading"
        sx={{
          width: '100%',
          py: { xs: 6, md: 8 },
          px: { xs: 2, sm: 3, md: 4 },
          backgroundColor: '#ffffff',
          scrollMarginTop: '80px',
        }}
      >
        <Container maxWidth="lg">
          <ScrollReveal>
            <SignalSection
              icon={<VolunteerActivismOutlinedIcon fontSize="small" />}
            >
              Nonprofit Filing
            </SignalSection>
            <Typography
              variant="h2"
              id="exempt-org-heading"
              sx={{
                fontSize: { xs: 28, sm: 34, md: 40 },
                fontWeight: 700,
                mb: 1.5,
              }}
            >
              Exempt Organization forms
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontSize: { xs: 16, sm: 18 }, mb: 3 }}
            >
              For nonprofits, charities, and other organizations that qualify
              for tax-exempt status.
            </Typography>

            <Paper
              variant="outlined"
              sx={{
                p: 2.5,
                mb: 4,
                borderRadius: 3,
                backgroundColor: '#f8fafc',
                borderColor: '#e2e8f0',
              }}
            >
              <Typography
                variant="h3"
                sx={{ fontSize: { xs: 16, sm: 18 }, fontWeight: 700, mb: 1 }}
              >
                When does an organization need this category?
              </Typography>
              <Typography variant="body1">
                When it wants to apply for federal tax-exempt status, report its
                annual activities and finances, or report unrelated business
                income.
              </Typography>
            </Paper>

            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ borderRadius: 3, borderColor: '#e1d8eb' }}
            >
              <Table>
                <TableHead sx={{ backgroundColor: '#f4f0ff' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, width: '35%' }}>
                      Organization's situation
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, width: '25%' }}>
                      Main form
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Purpose</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Tax-exempt organization with unrelated business taxable
                      income
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <Link
                        href="/tax-exempt/e-file-irs-form-990-t/ "
                        sx={{ color: '#0202ff' }}
                      >
                        Form 990-T
                      </Link>
                    </TableCell>
                    <TableCell>
                      Reports unrelated business income and applicable tax.
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#fafcff' }}>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Certain political organizations
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <Link
                        href="/tax-exempt/e-file-irs-form-1120-pol/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        Form 1120-POL
                      </Link>
                    </TableCell>
                    <TableCell>
                      Reports taxable political organization income.
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </ScrollReveal>
        </Container>
      </Box>

      {/* 4. Extension forms Section */}
      <Box
        component="section"
        id="extension-forms"
        aria-labelledby="extension-forms-heading"
        sx={{
          width: '100%',
          py: { xs: 6, md: 8 },
          px: { xs: 2, sm: 3, md: 4 },
          backgroundColor: '#fafcff',
          scrollMarginTop: '80px',
        }}
      >
        <Container maxWidth="lg">
          <ScrollReveal>
            <SignalSection
              icon={<EventAvailableOutlinedIcon fontSize="small" />}
            >
              Extension Filing
            </SignalSection>
            <Typography
              variant="h2"
              id="extension-forms-heading"
              sx={{
                fontSize: { xs: 28, sm: 34, md: 40 },
                fontWeight: 700,
                mb: 1.5,
              }}
            >
              Extension forms
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontSize: { xs: 16, sm: 18 }, mb: 3 }}
            >
              For businesses and organizations that need more time to file a tax
              return.
            </Typography>

            <Paper
              variant="outlined"
              sx={{
                p: 2.5,
                mb: 4,
                borderRadius: 3,
                backgroundColor: '#ffffff',
                borderColor: '#e2e8f0',
              }}
            >
              <Typography
                variant="h3"
                sx={{ fontSize: { xs: 16, sm: 18 }, fontWeight: 700, mb: 1 }}
              >
                When does a business owner need this category?
              </Typography>
              <Typography variant="body1">
                When the business or organization cannot complete its required
                tax return by the original deadline and is eligible to request
                an extension.
              </Typography>
            </Paper>

            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ borderRadius: 3, borderColor: '#e1d8eb', mb: 3 }}
            >
              <Table>
                <TableHead sx={{ backgroundColor: '#f4f0ff' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, width: '35%' }}>
                      Return to be extended
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        width: '25%',
                        textAlign: 'center',
                      }}
                    >
                      Extension form
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Typical use</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Row 1: Form 1120 + Form 7004 (rowSpan 4) */}
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Form 1120</TableCell>
                    <TableCell
                      rowSpan={4}
                      sx={{
                        fontWeight: 600,
                        textAlign: 'center',
                        verticalAlign: 'middle',
                        backgroundColor: '#ffffff',
                        borderRight: '1px solid #e1d8eb',
                        borderLeft: '1px solid #e1d8eb',
                      }}
                    >
                      <Link
                        href="/tax-extension/efile-irs-form-7004-business-tax-extension-online/ "
                        sx={{ color: '#0202ff' }}
                      >
                        Form 7004
                      </Link>
                    </TableCell>
                    <TableCell>Extension for a C corporation return.</TableCell>
                  </TableRow>

                  {/* Row 2: Form 1120-S */}
                  <TableRow sx={{ backgroundColor: '#fafcff' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Form 1120-S</TableCell>
                    <TableCell>
                      Extension for an S corporation return.
                    </TableCell>
                  </TableRow>

                  {/* Row 3: Form 1065 */}
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Form 1065</TableCell>
                    <TableCell>Extension for a partnership return.</TableCell>
                  </TableRow>

                  {/* Row 4: Form 1120-POL */}
                  <TableRow sx={{ backgroundColor: '#fafcff' }}>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Form 1120-POL
                    </TableCell>
                    <TableCell>
                      Extension for a Political Organization.
                    </TableCell>
                  </TableRow>

                  {/* Row 5: Form 990-T + Form 8868 */}
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Form 990-T</TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        textAlign: 'center',
                        verticalAlign: 'middle',
                        borderRight: '1px solid #e1d8eb',
                        borderLeft: '1px solid #e1d8eb',
                      }}
                    >
                      <Link
                        href="/tax-extension/efile-irs-form-8868-exempt-organization-tax-extension-online/ "
                        sx={{ color: '#0202ff' }}
                      >
                        Form 8868
                      </Link>
                    </TableCell>
                    <TableCell>
                      Extension for eligible exempt organization returns.
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            <Alert
              severity="info"
              sx={{ mb: 3, borderLeft: '3px solid #0288d1' }}
            >
              <strong>Key message for business owners:</strong> An extension
              generally gives additional time to file, not additional time to
              pay taxes owed. Form 7004 is a widely used extension form for
              business income tax returns, but it does not cover every tax
              return.
            </Alert>
          </ScrollReveal>
        </Container>
      </Box>

      {/* 5. Information Returns Section */}
      <Box
        component="section"
        id="information-returns"
        aria-labelledby="information-returns-heading"
        sx={{
          width: '100%',
          py: { xs: 6, md: 8 },
          px: { xs: 2, sm: 3, md: 4 },
          backgroundColor: '#ffffff',
          scrollMarginTop: '80px',
        }}
      >
        <Container maxWidth="lg">
          <ScrollReveal>
            <SignalSection icon={<PaymentsOutlinedIcon fontSize="small" />}>
              Payment Reporting
            </SignalSection>
            <Typography
              variant="h2"
              id="information-returns-heading"
              sx={{
                fontSize: { xs: 28, sm: 34, md: 40 },
                fontWeight: 700,
                mb: 1.5,
              }}
            >
              Information Returns
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontSize: { xs: 16, sm: 18 }, mb: 3 }}
            >
              For businesses that report payments, compensation, and other
              information to the IRS and recipients.
            </Typography>

            <Paper
              variant="outlined"
              sx={{
                p: 2.5,
                mb: 4,
                borderRadius: 3,
                backgroundColor: '#f8fafc',
                borderColor: '#e2e8f0',
              }}
            >
              <Typography
                variant="h3"
                sx={{ fontSize: { xs: 16, sm: 18 }, fontWeight: 700, mb: 1 }}
              >
                When does a business owner need this category?
              </Typography>
              <Typography variant="body1">
                When the business pays independent contractors, employees,
                vendors, or other recipients and must report certain payments or
                transactions
              </Typography>
            </Paper>

            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ borderRadius: 3, borderColor: '#e1d8eb', mb: 5 }}
            >
              <Table>
                <TableHead sx={{ backgroundColor: '#f4f0ff' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, width: '35%' }}>
                      Business situation
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, width: '25%' }}>
                      Main form
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Purpose</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Paying eligible independent contractors or reporting
                      certain other payments
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <Link
                        href="/information-returns/efile-irs-form-1099-nec/ "
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        Form 1099-NEC
                      </Link>
                    </TableCell>
                    <TableCell>Reports nonemployee compensation.</TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#fafcff' }}>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Reporting rent, interest, dividends, and other specified
                      payments
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <Link
                        href="/information-returns/efile-irs-form-1099-misc/ "
                        sx={{ color: '#0202ff' }}
                      >
                        Form 1099-MISC
                      </Link>
                    </TableCell>
                    <TableCell>Reports miscellaneous information.</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Reporting interest income
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <Link
                        href="/information-returns/efile-irs-form-1099-int/ "
                        sx={{ color: '#0202ff' }}
                      >
                        Form 1099-INT
                      </Link>
                    </TableCell>
                    <TableCell>Reports interest paid.</TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#fafcff' }}>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Reporting dividends and distributions
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <Link
                        href="/information-returns/efile-irs-form-1099-div/ "
                        sx={{ color: '#0202ff' }}
                      >
                        Form 1099-DIV
                      </Link>
                    </TableCell>
                    <TableCell>
                      Reports dividends and certain distributions.
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Reporting certain payment card or third-party network
                      transactions
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <Link
                        href="/information-returns/efile-irs-form-1099-k/ "
                        sx={{ color: '#0202ff' }}
                      >
                        Form 1099-K
                      </Link>
                    </TableCell>
                    <TableCell>
                      Reports qualifying payment transactions.
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#fafcff' }}>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Reporting wages paid to employees
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <Link
                        href="/information-returns/efile-irs-form-w2/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form W-2
                      </Link>
                    </TableCell>
                    <TableCell>
                      Reports employee wages and withholding.
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </ScrollReveal>
        </Container>
      </Box>
      {/* Essential Forms */}
      <Box
        component="section"
        aria-labelledby="essential-forms-heading"
        sx={{
          width: '100%',
          py: { xs: 6, md: 8 },
          px: { xs: 2, sm: 3, md: 4 },
          backgroundColor: '#fafcff',
          scrollMarginTop: '80px',
        }}
      >
        <Container maxWidth="lg">
          <ScrollReveal>
            <SignalSection icon={<AssignmentOutlinedIcon fontSize="small" />}>
              Required Forms
            </SignalSection>
            <Typography
              variant="h2"
              id="essential-forms-heading"
              sx={{
                fontSize: { xs: 28, sm: 34, md: 40 },
                fontWeight: 700,
                mb: 1.5,
              }}
            >
              Essential Forms:
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontSize: { xs: 16, sm: 18 }, fontWeight: 600, mb: 2 }}
            >
              Essential Tax Forms for Payee Information
            </Typography>

            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ borderRadius: 3, borderColor: '#e1d8eb', mb: 2 }}
            >
              <Table>
                <TableHead sx={{ backgroundColor: '#f4f0ff' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, width: '35%' }}>
                      Form
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Purpose</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <Link
                        href="/request-form-w9-online-free/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form W-9
                      </Link>
                    </TableCell>
                    <TableCell>
                      Collects a U.S. person's taxpayer identification number
                      and tax certification.
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#fafcff' }}>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <Link
                        href="/request-form-w8ben-online-free/ "
                        sx={{ color: '#0202ff' }}
                      >
                        Form W-8BEN
                      </Link>
                    </TableCell>
                    <TableCell>
                      Documents the foreign status of a non-U.S. individual and,
                      when applicable, supports a claim for treaty benefits.
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            <Typography variant="body2" sx={{ color: '#64748b' }}>
              These forms are generally provided to the requester or withholding
              agent, rather than filed with the IRS as standalone information
              returns.
            </Typography>
          </ScrollReveal>
        </Container>
      </Box>

      {/* Deadlines for Every Form Section */}
      <Box
        component="section"
        id="deadlines-for-every-form"
        aria-labelledby="deadlines-heading"
        sx={{
          width: '100%',
          py: { xs: 6, md: 9 },
          px: { xs: 2, sm: 3, md: 4 },
          backgroundColor: '#ffffff',
          scrollMarginTop: '80px',
        }}
      >
        <Container maxWidth="lg">
          <ScrollReveal>
            <Box textAlign="center" sx={{ mb: 5 }}>
              <SignalSection icon={<EventNoteOutlinedIcon fontSize="small" />}>
                Filing Due Dates
              </SignalSection>
              <Typography
                variant="h2"
                id="deadlines-heading"
                sx={{
                  fontSize: { xs: 30, sm: 36, md: 44 },
                  fontWeight: 700,
                  mb: 2,
                }}
              >
                Deadlines for every form
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { xs: 16, sm: 18, md: 20 },
                  maxWidth: 820,
                  mx: 'auto',
                }}
              >
                Find the filing deadlines for each tax form, including due
                dates, extension options, and any additional filing requirements
                that apply to your business.
              </Typography>
            </Box>

            <TabContext value={activeDeadlineTab}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  width: '100%',
                  mb: 4,
                }}
              >
                <Box
                  sx={{
                    borderRadius: 8,
                    border: '1px solid #ccc',
                    backgroundColor: '#ffffff',
                    p: 0.5,
                    display: 'inline-flex',
                    maxWidth: '100%',
                  }}
                >
                  <Tabs
                    value={activeDeadlineTab}
                    onChange={handleDeadlineTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    sx={{
                      '& .MuiTabs-indicator': {
                        backgroundColor: 'transparent !important',
                      },
                      '& .Mui-selected': {
                        color: '#ffffff !important',
                        backgroundColor: '#333 !important',
                        borderRadius: 8,
                      },
                      '& .MuiTab-root': {
                        textTransform: 'none',
                        borderRadius: 8,
                        color: '#333',
                        fontWeight: 600,
                        px: { xs: 2.5, sm: 3.5 },
                        py: 1,
                        minHeight: 44,
                      },
                    }}
                  >
                    <Tab label="Business" value="1" />
                    <Tab label="Employment" value="2" />
                    <Tab label="Exempt" value="3" />
                    <Tab label="Extensions" value="4" />
                    <Tab label="Information" value="5" />
                  </Tabs>
                </Box>
              </Box>

              {/* Tab 1: Business */}
              <TabPanel value="1" sx={{ p: 0 }}>
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ borderRadius: 3, borderColor: '#e1d8eb' }}
                >
                  <Table>
                    <TableHead sx={{ backgroundColor: '#f4f0ff' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, width: '35%' }}>
                          Form
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, width: '35%' }}>
                          Due Date (Calendar year)
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Due Date (Fiscal year)
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Form 1120
                        </TableCell>
                        <TableCell>April 15</TableCell>
                        <TableCell>15th Day of 4th Month</TableCell>
                      </TableRow>
                      <TableRow sx={{ backgroundColor: '#fafcff' }}>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Form 1120-S
                        </TableCell>
                        <TableCell>March 15</TableCell>
                        <TableCell>15th day of 3rd Month</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Form 1065
                        </TableCell>
                        <TableCell>March 15</TableCell>
                        <TableCell>15th day of 3rd Month</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </TabPanel>

              {/* Tab 2: Employment */}
              <TabPanel value="2" sx={{ p: 0 }}>
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ borderRadius: 3, borderColor: '#e1d8eb' }}
                >
                  <Table>
                    <TableHead sx={{ backgroundColor: '#f4f0ff' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, width: '25%' }}>
                          Form
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, width: '35%' }}>
                          Due Date (Calendar year)
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Rules and Extensions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Form 941</TableCell>
                        <TableCell>
                          Q1: April 30
                          <br />
                          Q2: July 31
                          <br />
                          Q3: October 31
                          <br />
                          Q4: February 1
                        </TableCell>
                        <TableCell>
                          Last day of the month after each quarter ends.
                        </TableCell>
                      </TableRow>
                      <TableRow sx={{ backgroundColor: '#fafcff' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Form 940</TableCell>
                        <TableCell>February 1</TableCell>
                        <TableCell>
                          February 10 if you deposited all FUTA tax on time
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Form 944</TableCell>
                        <TableCell>February 1</TableCell>
                        <TableCell>
                          Annual return, only if the IRS has notified you to
                          file it.
                        </TableCell>
                      </TableRow>
                      <TableRow sx={{ backgroundColor: '#fafcff' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Form 943</TableCell>
                        <TableCell>February 1</TableCell>
                        <TableCell>
                          Annual return for agricultural employees
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Form 945</TableCell>
                        <TableCell>February 1</TableCell>
                        <TableCell>
                          Annual return for nonpayroll withholding.
                        </TableCell>
                      </TableRow>
                      <TableRow sx={{ backgroundColor: '#fafcff' }}>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Form 941 Schedule R
                        </TableCell>
                        <TableCell>With Form 941</TableCell>
                        <TableCell>
                          Filed with each Form 941 by aggregate filers.
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Form 940 Schedule R
                        </TableCell>
                        <TableCell>With Form 940</TableCell>
                        <TableCell>
                          Filed with Form 940 by aggregate filers.
                        </TableCell>
                      </TableRow>
                      <TableRow sx={{ backgroundColor: '#fafcff' }}>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Forms 941-X, 943-X, 944-X, 945-X, 940-Amended
                        </TableCell>
                        <TableCell>Amended return</TableCell>
                        <TableCell>
                          File as soon as you discover it. Time limits apply, so
                          check the form instructions.
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </TabPanel>

              {/* Tab 3: Exempt */}
              <TabPanel value="3" sx={{ p: 0 }}>
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ borderRadius: 3, borderColor: '#e1d8eb' }}
                >
                  <Table>
                    <TableHead sx={{ backgroundColor: '#f4f0ff' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, width: '35%' }}>
                          Form
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, width: '35%' }}>
                          Due Date (Calendar year)
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Due Date (Fiscal year)
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Form 990-T
                        </TableCell>
                        <TableCell>
                          April 15
                          <br />
                          May 15
                        </TableCell>
                        <TableCell>
                          15th day of 4th month
                          <br />
                          15th Day of 5th Month
                        </TableCell>
                      </TableRow>
                      <TableRow sx={{ backgroundColor: '#fafcff' }}>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Form 1120-POL
                        </TableCell>
                        <TableCell>April 15</TableCell>
                        <TableCell>15th day of 4th Month</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </TabPanel>

              {/* Tab 4: Extensions */}
              <TabPanel value="4" sx={{ p: 0 }}>
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ borderRadius: 3, borderColor: '#e1d8eb' }}
                >
                  <Table>
                    <TableHead sx={{ backgroundColor: '#f4f0ff' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, width: '35%' }}>
                          Form
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, width: '35%' }}>
                          Due Date (Calendar year)
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Due Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Form 7004
                        </TableCell>
                        <TableCell>September 15</TableCell>
                        <TableCell>Automatic 6-month Extension</TableCell>
                      </TableRow>
                      <TableRow sx={{ backgroundColor: '#fafcff' }}>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Form 8868
                        </TableCell>
                        <TableCell>November 15</TableCell>
                        <TableCell>Automatic 6-month Extension</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </TabPanel>

              {/* Tab 5: Information */}
              <TabPanel value="5" sx={{ p: 0 }}>
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ borderRadius: 3, borderColor: '#e1d8eb' }}
                >
                  <Table>
                    <TableHead sx={{ backgroundColor: '#f4f0ff' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, width: '30%' }}>
                          Form
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, width: '35%' }}>
                          Due Date (Calendar year)
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Due Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Form W-2 and Form W-3
                        </TableCell>
                        <TableCell>January 31</TableCell>
                        <TableCell>
                          Due to employees and the Social Security
                          Administration
                        </TableCell>
                      </TableRow>
                      <TableRow sx={{ backgroundColor: '#fafcff' }}>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Form 1099-NEC
                        </TableCell>
                        <TableCell>January 31</TableCell>
                        <TableCell>Due to recipients and the IRS.</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Forms 1099-MISC, 1099-INT, 1099-DIV, <br />
                          1099-K
                        </TableCell>
                        <TableCell>
                          Recipients: January 31
                          <br />
                          IRS copy: February 28 on paper, March 31 if filed
                          electronically. Some boxes have a February 15
                          recipient date
                        </TableCell>
                        <TableCell>
                          IRS copy: February 28 on paper, March 31 if filed
                          electronically. Some boxes have a February 15
                          recipient date{' '}
                        </TableCell>
                      </TableRow>
                      <TableRow sx={{ backgroundColor: '#fafcff' }}>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Corrected 1099 or W-2
                        </TableCell>
                        <TableCell>As soon as possible</TableCell>
                        <TableCell>
                          File once you find the error. Penalties are lower the
                          sooner you correct.
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </TabPanel>
            </TabContext>
            <Alert
              severity="info"
              sx={{
                my: 3,
                borderLeft: '3px solid #0288d1',
                '& ul': {
                  m: 0,
                  pl: 2.5,
                  mt: 0.5,
                },
                '& li': {
                  mt: 0.5,
                },
              }}
            >
              <strong>Remember:</strong>
              <ul>
                <li>
                  If your filing deadline falls on a Saturday, Sunday, or legal
                  holiday, it moves to the next business day.
                </li>
                <li>
                  An extension gives you more time to file, not to pay. Pay any
                  taxes owed by the original due date to avoid penalties and
                  interest.
                </li>
              </ul>
            </Alert>
          </ScrollReveal>
        </Container>
      </Box>

      {/* Testimonials Section */}
      <Box
        component="section"
        aria-labelledby="testimonials-heading"
        sx={{
          py: 12,
          px: { xs: 3, sm: 5, md: 6 },
          textAlign: 'center',
          backgroundColor: '#fafcff',
        }}
      >
        <ScrollReveal>
          <SignalSection icon={<StarBorderOutlinedIcon fontSize="small" />}>
            Testimonials
          </SignalSection>
          <Typography
            variant="h2"
            id="testimonials-heading"
            sx={{
              fontSize: { xs: 22, sm: 30, md: 42, lg: 58 },
              fontWeight: 700,
              mb: 5,
            }}
          >
            Trusted by businesses and tax filers
          </Typography>
        </ScrollReveal>

        <ScrollReveal delay={200}>
          <ReviewRatings />
        </ScrollReveal>

        <Grid
          container
          spacing={3}
          maxWidth="xl"
          alignItems="flex-start"
          sx={{ mx: 'auto', mt: 5, textAlign: 'left' }}
        >
          <Grid size={{ xs: 12, md: 4.2 }} sx={{ px: 0 }}>
            <ScrollReveal delay={300}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 4.5 },
                  minHeight: { md: 220 },
                  borderRadius: '20px',
                  border: '1px solid #E2E8F0',
                  backgroundColor: '#ffffff',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
              >
                <Box>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#475569',
                      lineHeight: 1.6,
                      fontSize: '1rem',
                      mb: 2.5,
                    }}
                  >
                    very clear. very colorful. large print. very intuitive to
                    figure out. all the forms I need for a business and a
                    foundation all in one website.
                  </Typography>
                </Box>
                <Box>
                  <Rating
                    value={5}
                    readOnly
                    size="small"
                    sx={{ color: '#64748B', mb: 1 }}
                  />
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 700,
                      color: '#0F172A',
                    }}
                  >
                    Michael Tracy
                  </Typography>
                </Box>
              </Paper>
            </ScrollReveal>
          </Grid>

          <Grid size={{ xs: 12, md: 3.6 }} sx={{ px: 0 }}>
            <ScrollReveal delay={400}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 3 },
                  minHeight: { md: 180 },
                  borderRadius: '16px',
                  backgroundColor: '#F3E8FF',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
              >
                <Box>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#334155',
                      lineHeight: 1.55,
                      fontSize: '0.925rem',
                      mb: 2,
                    }}
                  >
                    The site was user-friendly and led me through filling out
                    the forms - - all the way to submitting them.
                  </Typography>
                </Box>
                <Box>
                  <Rating
                    value={5}
                    readOnly
                    size="small"
                    sx={{ color: '#64748B', mb: 1 }}
                  />
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 700,
                      color: '#0F172A',
                    }}
                  >
                    Pamela Russell
                  </Typography>
                </Box>
              </Paper>
            </ScrollReveal>
          </Grid>

          <Grid size={{ xs: 12, md: 4.2 }} sx={{ px: 0 }}>
            <ScrollReveal delay={500}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 4.5 },
                  minHeight: { md: 220 },
                  borderRadius: '20px',
                  border: '1px solid #E2E8F0',
                  backgroundColor: '#ffffff',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
              >
                <Box>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#475569',
                      lineHeight: 1.6,
                      fontSize: '1rem',
                      mb: 2.5,
                    }}
                  >
                    I am very satisfied with the system it is easy to use and
                    affordable for small business owners.
                  </Typography>
                </Box>
                <Box>
                  <Rating
                    value={5}
                    readOnly
                    size="small"
                    sx={{ color: '#64748B', mb: 1 }}
                  />
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 700,
                      color: '#0F172A',
                    }}
                  >
                    Erika Quiroz
                  </Typography>
                </Box>
              </Paper>
            </ScrollReveal>
          </Grid>
        </Grid>
      </Box>
      {/* Frequently Asked Questions Section */}
      <Box
        component="section"
        id="faqs"
        aria-labelledby="faq-section-title"
        sx={{
          py: { xs: 8, md: 12 },
          px: { xs: 2, sm: 3, md: 4 },
          // borderTop: '1px solid #e2e8f0',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <ScrollReveal>
            <Box textAlign="center" sx={{ mb: 5 }}>
              <SignalSection icon={<LiveHelpOutlinedIcon fontSize="small" />}>
                FAQs
              </SignalSection>
              <Typography
                variant="h2"
                id="faq-section-title"
                sx={{
                  fontSize: { xs: 30, sm: 36, md: 44 },
                  fontWeight: 700,
                }}
              >
                Frequently Asked Questions
              </Typography>
            </Box>
          </ScrollReveal>

          <Box
            itemScope
            itemType="https://schema.org/FAQPage"
            sx={{
              textAlign: 'left',
              maxWidth: 900,
              mx: 'auto',
              '& .MuiAccordionSummary-content': {
                fontWeight: 600,
                fontSize: { xs: 16, sm: 18, md: 20 },
              },
            }}
          >
            {/* Question 1 */}
            <ScrollReveal>
              <Accordion
                component="article"
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  boxShadow: 'none',
                  backgroundColor: 'transparent',
                  borderTop: '1px solid #e2e8f0',
                  '&::before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  itemProp="name"
                  expandIcon={<AddIcon />}
                  sx={{
                    px: 0,
                    py: 1,
                    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
                      transform: 'rotate(45deg)',
                    },
                  }}
                >
                  1. What business tax forms do I need to file?
                </AccordionSummary>
                <AccordionDetails
                  sx={{ px: 0, pt: 0, pb: 2.5 }}
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box itemProp="text" sx={{ fontSize: 16, lineHeight: 1.6 }}>
                    <Typography variant="body1">
                      The business tax forms you need depend on your business
                      structure and tax obligations. C corporations generally
                      file Form 1120, S corporations file Form 1120-S, and
                      partnerships generally file Form 1065.
                    </Typography>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </ScrollReveal>

            {/* Question 2 */}
            <ScrollReveal delay={100}>
              <Accordion
                component="article"
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  boxShadow: 'none',
                  backgroundColor: 'transparent',
                  borderTop: '1px solid #e2e8f0',
                  '&::before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  itemProp="name"
                  expandIcon={<AddIcon />}
                  sx={{
                    px: 0,
                    py: 1,
                    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
                      transform: 'rotate(45deg)',
                    },
                  }}
                >
                  2. What employment tax forms does my business need to file?
                </AccordionSummary>
                <AccordionDetails
                  sx={{ px: 0, pt: 0, pb: 2.5 }}
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box itemProp="text" sx={{ fontSize: 16, lineHeight: 1.6 }}>
                    <Typography variant="body1">
                      Your employment tax forms depend on your payroll
                      activities. Employers generally use Form 941 for quarterly
                      payroll taxes, Form 940 for annual federal unemployment
                      tax, or Form 944 if the IRS instructs them to file
                      annually. Agricultural employers generally use Form 943.
                    </Typography>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </ScrollReveal>

            {/* Question 3 */}
            <ScrollReveal delay={200}>
              <Accordion
                component="article"
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  boxShadow: 'none',
                  backgroundColor: 'transparent',
                  borderTop: '1px solid #e2e8f0',
                  '&::before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  itemProp="name"
                  expandIcon={<AddIcon />}
                  sx={{
                    px: 0,
                    py: 1,
                    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
                      transform: 'rotate(45deg)',
                    },
                  }}
                >
                  3. What information returns does my business need to file?
                </AccordionSummary>
                <AccordionDetails
                  sx={{ px: 0, pt: 0, pb: 2.5 }}
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box itemProp="text" sx={{ fontSize: 16, lineHeight: 1.6 }}>
                    <Typography variant="body1">
                      Your business may need information returns based on
                      payments and transactions during the year. Common examples
                      include Form W-2 for employee wages, Form 1099-NEC for
                      reportable nonemployee compensation, Form 1099-MISC for
                      certain other payments, and Form 1099-INT for interest
                      income.
                    </Typography>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </ScrollReveal>

            {/* Question 4 */}
            <ScrollReveal delay={300}>
              <Accordion
                component="article"
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  boxShadow: 'none',
                  backgroundColor: 'transparent',
                  borderTop: '1px solid #e2e8f0',
                  borderBottom: '1px solid #e2e8f0',
                  '&::before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  itemProp="name"
                  expandIcon={<AddIcon />}
                  sx={{
                    px: 0,
                    py: 1,
                    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
                      transform: 'rotate(45deg)',
                    },
                  }}
                >
                  4. What is Form W-9 used for in a business?
                </AccordionSummary>
                <AccordionDetails
                  sx={{ px: 0, pt: 0, pb: 2.5 }}
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box itemProp="text" sx={{ fontSize: 16, lineHeight: 1.6 }}>
                    <Typography variant="body1">
                      Form W-9 is used to request a U.S. person's taxpayer
                      identification number and certification. Businesses
                      commonly collect it from contractors and other payees to
                      help prepare required information returns. The requester
                      generally keeps the completed form rather than filing it
                      with the IRS.
                    </Typography>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </ScrollReveal>
          </Box>
        </Container>

        {/* Footer Wave Ambient Background */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            transform: {
              xs: 'translate(-15%, -35%) rotate(145deg)',
              sm: 'translate(25%, 25%) rotate(135deg)',
              md: 'translate(35%, 25%) rotate(135deg)',
            },
            pointerEvents: 'none',
            opacity: 0.35,
          }}
        >
          <FooterWave />
        </Box>
      </Box>
    </AppLayout>
  );
};

export default BusinessTaxFilingFinder;
